import { Component, OnInit, Input, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { NgxSpinnerService } from "ngx-spinner";
import BaseComponent from "src/app/core/base-component";
import { ApiServiceUf } from "src/app/core/services/uf.service";
import { ApiServiceStore } from "src/app/core/services/store.service";
import { CpfCnpjValidator } from "src/app/core/validators/cpfCnpj.validator";
import Swal, { SweetAlertType } from "sweetalert2";
import { ProductConfirmSolicitationDialog } from "../product-confirm-solicitation-dialog/product-confirm-solicitation-dialog.component";
import { MatStepper } from "@angular/material/stepper";
import * as moment from "moment";
import { cnpj, cpf } from "cpf-cnpj-validator";

@Component({
  selector: "product-solicitation-dialog",
  templateUrl: "./product-solicitation-dialog.component.html",
  styleUrls: ["./product-solicitation-dialog.component.scss"]
})
export class ProductSolicitationDialog extends BaseComponent implements OnInit {
  @ViewChild("steppertreestep", { static: true }) private myStepper: MatStepper;

  @Input("productId") productId: string;
  @Input("productName") productName: string;
  @Input("siteName") siteName: string;
  @Input("documentType") documentType: string;
  @Input("restriction") restriction: boolean;
  @Input("selectedProduct") selectedProduct: any;

  form: FormGroup;
  terms: boolean;
  optionsMoney = {
    prefix: "R$ ",
    thousands: ".",
    decimal: ",",
    allowNegative: false,
    align: "left"
  };

  states: Array<{ name: string; id: string }> = this.apiServiceUf.getUf();
  checkAcc: boolean = false;
  cepByCNPJ: boolean;
  dateInput: string;

  isProductRestricted: boolean = false;

  newProposalPermissionMessage: string;
  showSpinner: boolean = false;
  showErrorIcon: boolean = false;
  showSuccessIcon: boolean = false;

  constructor(
    public matDialogRef: MatDialogRef<ProductSolicitationDialog>,
    public formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private apiServiceStore: ApiServiceStore,
    private dialog: MatDialog,
    private apiServiceUf: ApiServiceUf
  ) {
    super();
  }

  ngOnInit() {
    this.createForm();
    this.checkProductEligibility();
    this.disableFields();
  }

  createForm() {
    this.form = this.formBuilder.group({
      product_uuid: [this.productId],
      name: ["", Validators.required],
      document: ["", [Validators.required, CpfCnpjValidator.validateCpfCpnj]],
      date_of_birth: ["", [Validators.required, this.validateBirthdate]],
      email: ["", [Validators.required, Validators.email]],
      phone: ["", Validators.required],
      address: this.formBuilder.group({
        street: ["", Validators.required],
        number: ["", Validators.required],
        complement: [""],
        neighborhood: ["", Validators.required],
        city: ["", Validators.required],
        state: ["", Validators.required],
        zipcode: ["", [Validators.required, Validators.minLength(9)]]
      }),
      responsible: [""],
      value: ["", Validators.required],
      isCheckingAccount: [false],
      accept_terms: [false],
      checkingAccountInfo: this.formBuilder.group({
        account: [""],
        agency: [""]
      })
    });

    this.form.get("date_of_birth").valueChanges.subscribe(() => {
      this.checkProductEligibility();
    });

    this.form.get("address.state").valueChanges.subscribe(() => {
      this.checkProductEligibility();
    });
  }

  onDocumentInputChange() {
    const documentValue = this.form.get("document").value.replace(/\D+/g, "");
    const isValidCPF = cpf.isValid(documentValue);
    const isValidCNPJ = cnpj.isValid(documentValue);
    const documentControl = this.form.get("document");

    if (documentControl.dirty) {
      this.showErrorIcon = false;
      this.showSuccessIcon = false;
      this.disableFields();
    }

    if (isValidCPF || isValidCNPJ) {
      this.getProposalPermission(documentValue);
      this.showSpinner = true;
      this.showSuccessIcon = false;
      this.showErrorIcon = false;
    }
  }

  getProposalPermission(documentValue: string) {
    this.apiServiceStore.enableNewProductProposalPermission(this.siteName, documentValue, this.productId).subscribe({
      next: response => {
        this.showSpinner = false;
        const result = response?.data.filter(newProposal => this.productId === newProposal.productId) || [];

        if (result.length > 0) {
          this.form.get("document").markAsTouched();
          this.newProposalPermissionMessage = result[0].message;
          this.showErrorIcon = true;
        } else {
          this.enableFields();
          this.showSuccessIcon = true;
        }
      },
      error: error => {
        this.showSpinner = false;
        if (error.statusCode === 409) {
          this.form.get("document").markAsTouched();
          this.form.get("document").setErrors({
            errorGetDocument: true
          });
          this.showErrorIcon = true;
          this.newProposalPermissionMessage = error.message;
        }
        throw new Error(error.message);
      }
    });
  }

  enableFields() {
    for (const controlName in this.form.controls) {
      if (controlName !== "document") {
        this.form.get(controlName).enable();
      }
    }
  }

  disableFields() {
    for (const controlName in this.form.controls) {
      if (controlName !== "document") {
        this.form.get(controlName).disable();
      }
    }
  }

  get formProduct() {
    return this.form.value;
  }

  cssError(field: string) {
    const useDangerClass = this.hasError(field, "errorGetDocument");

    if (useDangerClass) {
      return {
        "is-danger": true,
        "is-invalid": false
      };
    }

    return {
      "is-danger": false,
      "is-invalid": this.hasError(field)
    };
  }

  validateBirthdate(input: FormControl) {
    const birthdate = input.value;
    const parsedDate = moment(birthdate, "DD-MM-YYYY");
    const currentDate = moment();

    if (birthdate.dirty) {
      return { required: true };
    }

    if (!birthdate) {
      return { required: true };
    }

    if (!parsedDate.isValid()) {
      return { invalidDate: true };
    }

    if (parsedDate.isAfter(currentDate, "day")) {
      return { invalidDate: true };
    }

    return null;
  }

  checkProductEligibility() {
    const state = this.form.get("address.state").value;
    const birthdate = this.form.get("date_of_birth").value;
    const parsedDate = moment(birthdate, "DD-MM-YYYY");
    const stateRestrictions = ["PB", "GO"].includes(state);
    const ageOver60 = parsedDate.isBefore(moment().subtract(60, "years"));

    if (stateRestrictions && ageOver60 && this.restriction) {
      this.apiServiceStore.getStorePublic(this.siteName).subscribe(
        response => {
          if (response && response?.categories) {
            this.isProductRestricted = true;
            this.form.get("date_of_birth").setErrors({ invalidProducts: true });
            return;
          }
          this.isProductRestricted = false;
        },
        error => {
          console.error("Erro na API", error);
          this.isProductRestricted = false;
        }
      );
    } else {
      this.isProductRestricted = false;
    }
  }

  closeDialog(close: boolean) {
    this.matDialogRef.close({ close: close });
  }

  getCep() {
    const zipcode = this.field("address.zipcode");
    const value = zipcode.value.replace(/\D/g, "");

    if (value.length === 8) {
      this.spinner.show();
      this.apiServiceStore.getCep(value).subscribe({
        next: res => {
          this.field("address.street").setValue(res.street);
          this.field("address.city").setValue(res.city);
          this.field("address.neighborhood").setValue(res.neighborhood);
          this.field("address.state").setValue(res.state);
        },
        error: () => {
          this.spinner.hide();
          this.alert("CEP não reconhecido", "warning");
        },
        complete: () => {
          this.spinner.hide();
        }
      });
    } else zipcode.setErrors({ invalidCep: true });
  }

  alert(title: string, icon: SweetAlertType) {
    Swal.fire({
      type: icon,
      title: title,
      showConfirmButton: false,
      timer: 2000
    });
  }

  send() {
    this.spinner.show();

    if (!this.form.valid) {
      this.spinner.hide();
      this.form.markAllAsTouched();
      this.alert("Formulário inválido", "warning");
      return;
    }

    const data = this.form.value;

    if (data.isCheckingAccount) {
      if (!data.checkingAccountInfo.account) {
        this.spinner.hide();
        this.alert("Preencha o Número da Conta", "warning");
        return;
      }

      if (!data.checkingAccountInfo.agency) {
        this.spinner.hide();
        this.alert("Preencha o Número da Agência", "warning");
        return;
      }
    }

    if (!data.accept_terms) {
      this.spinner.hide();
      this.alert("Aceite os Termos de Uso", "warning");
      return;
    }

    data.value = Number(data.value);
    data.document_type = this.documentType;
    data.isCheckingAccount = JSON.parse(data.isCheckingAccount);

    this.apiServiceStore.dealStorePublic(this.siteName, data).subscribe({
      next: () => {
        this.spinner.hide();
        this.openModalConfirmSolicitation();
        this.closeDialog(true);
      },
      error: error => {
        this.spinner.hide();

        if (error.message == 'duplicate key value violates unique constraint "unique_cnpj_client"') {
          this.alert("Formulário Inválido", "warning");
          this.form.get("document").setErrors({ alreadyRecord: true });
          return;
        }

        this.alert("Ocorreu um problema ao salvar!", "warning");
        throw new Error(error.mesage);
      }
    });
  }

  checkTerms() {
    this.terms = !this.terms;
    this.field("accept_terms").setValue(this.terms);
  }

  openModalConfirmSolicitation() {
    this.dialog.open(ProductConfirmSolicitationDialog, {
      width: "500px"
    });
  }
}
